/*
 * DESIGN: Forge & Circuit — Neo-Brutalist Dark Tech
 * Navbar: Fixed top, dark glass-morphism, thick bottom border accent.
 * Electric blue brand accent on logo "AI". Ember orange CTA button.
 */
import { useState } from "react";
import { Menu, X, Phone } from "lucide-react";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { label: "Services", href: "#services" },
    { label: "AI School", href: "#ai-survival-school" },
    { label: "Subsidiaries", href: "#subsidiaries" },
    { label: "About", href: "#about" },
    { label: "Contact", href: "#contact" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b-[3px] border-electric/30 bg-navy-deep/80 backdrop-blur-xl">
      <div className="container flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 group">
          <div className="w-8 h-8 md:w-10 md:h-10 border-[3px] border-electric rounded-sm flex items-center justify-center bg-electric/10 group-hover:bg-electric/20 transition-colors">
            <span className="font-heading font-bold text-electric text-sm md:text-base">BC</span>
          </div>
          <div className="font-heading font-bold text-lg md:text-xl tracking-tight">
            <span className="text-white">BLUE COLLAR</span>{" "}
            <span className="text-electric">AI</span>
          </div>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-5 lg:gap-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="font-heading text-sm font-medium text-steel-light hover:text-white transition-colors relative group"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-electric group-hover:w-full transition-all duration-300" />
            </a>
          ))}
          <a
            href="#contact"
            className="flex items-center gap-2 px-5 py-2.5 bg-ember text-white font-heading font-semibold text-sm border-[3px] border-ember hover:bg-ember/90 hover:border-ember/80 transition-all active:translate-y-[1px]"
          >
            <Phone className="w-4 h-4" />
            Get Started
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-12 h-12 flex items-center justify-center border-[2px] border-steel/30 text-white hover:border-electric transition-colors"
          aria-label="Toggle navigation menu"
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Menu — CSS grid-rows height transition (no JS animation lib) */}
      <div
        id="mobile-menu"
        className={`md:hidden grid overflow-hidden bg-navy-deep transition-[grid-template-rows,opacity] duration-300 ease-out ${
          mobileOpen
            ? "grid-rows-[1fr] opacity-100 border-t-[2px] border-electric/20"
            : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="container py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                tabIndex={mobileOpen ? 0 : -1}
                className="font-heading text-base font-medium text-steel-light hover:text-white py-2 border-b border-border/30 transition-colors"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setMobileOpen(false)}
              tabIndex={mobileOpen ? 0 : -1}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-ember text-white font-heading font-semibold text-sm border-[3px] border-ember mt-2"
            >
              <Phone className="w-4 h-4" />
              Get Started
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}
